<template>
  <div
    aria-hidden="true"
    style="
      position: absolute;
      width: 0;
      height: 0;
      overflow: hidden;
      pointer-events: none;
      visibility: hidden;
    "
  >
    <img
      v-for="(attrs, i) in preloadedAttrs"
      :key="i"
      v-bind="attrs"
      fetchpriority="low"
      decoding="async"
      loading="eager"
      alt=""
    />
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, shallowRef } from 'vue'
import { useImage } from '#imports'
import { usePipe, PipeDisplayMode, type NavigationEventPayload } from '@render/composables/usePipe'
import { walkSchema, createImageExtractor } from '@render/lib/schemaWalker'
import { resolveImagePreset } from '@render/composables/useImagePreset'
import { getDirectLinkedPageIds, walkPageWithSharedNodes } from '@render/lib/linkExtractor'
import type { Page } from '@api/types.gen'

const pipe = usePipe()
const img = useImage()

type ImgAttrs = NonNullable<ReturnType<typeof resolveImagePreset>>['imgAttrs']
const preloadedAttrs = shallowRef<ImgAttrs[]>([])

// No preloading while the visitor is picking a plan or paying: the offer cards
// and the payment SDK with its frames get the connection to themselves instead
// of sharing it with the images of the checkout, upsell and thank-you pages
// after them. Either a funnel marker or a component is enough to tell, as a
// plan selection can be built without an Offer Select and a page can go
// unmarked.
//
// Marker keys as in the admin's funnel-marker list (renaming one there stops
// it matching here).
const NO_PRELOAD_FUNNEL_MARKERS: ReadonlySet<string> = new Set([
  'Plan Selection',
  'Origin Order Checkout',
])

// Registry keys (`node.type`), matched anywhere on the page, inside shared
// nodes too.
const NO_PRELOAD_COMPONENT_TYPES: ReadonlySet<string> = new Set([
  'OfferSelect',
  'OfferSelectV2',
  'Checkout',
  'PrimerCheckout',
  'YunoCheckout',
])

const isNoPreloadPage = (page: Page): boolean => {
  if (page.funnelMarkers?.some((key) => NO_PRELOAD_FUNNEL_MARKERS.has(key))) return true
  let found = false
  walkPageWithSharedNodes(page, pipe.getSharedNodeById, [
    {
      process: (node) => {
        if (NO_PRELOAD_COMPONENT_TYPES.has(node.type)) found = true
      },
      getResult: () => found,
    },
  ])
  return found
}

const recompute = (payload: NavigationEventPayload) => {
  if (!import.meta.client) return
  if (pipe.currentMode.value === PipeDisplayMode.EDITOR) {
    preloadedAttrs.value = []
    return
  }

  const startPage = pipe.getPageById(payload.pageId)
  if (!startPage || isNoPreloadPage(startPage)) {
    preloadedAttrs.value = []
    return
  }

  // Pages we never want to preload images for: the page we're already on
  // (already loaded) plus any page in the visit history (stored in
  // `variables['global.visitedPageIds']` so visibility rules and integrations
  // can read it too — see usePipe.ts markPageVisited).
  const visitedRaw = pipe.variables.value['global.visitedPageIds']
  const visited = Array.isArray(visitedRaw) ? (visitedRaw as string[]) : []
  const skip = new Set<string>([payload.pageId, ...visited])
  const queued = new Set<string>(skip)
  const candidates: Page[] = []

  // BFS over the navigation graph. Landing pages are image-heavy, so when the
  // current page is a landing page we only prefetch the immediate next pages
  // (depth 1); elsewhere we look two hops ahead (depth 1 + their direct links,
  // depth 2). Skip already-visited / already-queued ids before they ever enter
  // the queue so the visited filter and the dedup filter share the same
  // membership check.
  const maxDepth = startPage.isLanding ? 1 : 2
  type Frame = { page: Page; depth: number }
  const queue: Frame[] = [{ page: startPage, depth: 0 }]

  while (queue.length) {
    const { page, depth } = queue.shift()!
    if (depth >= maxDepth) continue
    const targets = getDirectLinkedPageIds(page, pipe.getSharedNodeById)
    for (const targetId of targets) {
      if (queued.has(targetId)) continue
      queued.add(targetId)
      const targetPage = pipe.getPageById(targetId)
      if (!targetPage) continue
      candidates.push(targetPage)
      queue.push({ page: targetPage, depth: depth + 1 })
    }
  }

  if (candidates.length === 0) {
    preloadedAttrs.value = []
    return
  }

  const imageExtractor = createImageExtractor()
  for (const page of candidates) {
    walkSchema(page, [imageExtractor])
    walkPageWithSharedNodes(page, pipe.getSharedNodeById, [imageExtractor])
  }

  preloadedAttrs.value = imageExtractor
    .getResult()
    .map((data) => (data.id ? resolveImagePreset(img, data)?.imgAttrs : null))
    .filter((attrs): attrs is ImgAttrs => Boolean(attrs))
}

let unsubscribe: (() => void) | null = null

onMounted(() => {
  unsubscribe = pipe.subscribe('navigation:show', recompute)
})

onBeforeUnmount(() => {
  unsubscribe?.()
  unsubscribe = null
})
</script>
